import React, { useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import {
    Table,
    Column,
    AutoSizer,
    InfiniteLoader,
    defaultTableRowRenderer
} from 'react-virtualized'
import 'react-virtualized/styles.css'
import './custom-table-style.css'
import classNames from 'classnames'
import { getEvenRowBackground, getOddRowBackground } from './styles'
import { Loading } from '..'

const useStyles = makeStyles(theme => ({
    root: {
        color: theme.textPrimary,
        fontSize: 13,
        'will-change': 'transform'
    },
    wrapper: { position: 'relative', overflow: 'auto' },
    grid: {
        border: theme.border,
        borderRadius: '0 0 7px 7px',
        "& [role='gridcell']": {
            whiteSpace: 'pre-line'
        }
    },
    header: {
        textTransform: 'none',
        color: theme.textPrimary,
        fontWeight: 'normal',
        '& :hover': {
            color: theme.textSecondary
        },
        '& svg': {
            height: 18,
            width: 18
        }
    },
    colCentered: {
        display: 'flex',
        justifyContent: 'center'
    },
    evenRow: {
        background: getEvenRowBackground(theme),
        padding: '0 16px'
    },
    oddRow: {
        background: getOddRowBackground(theme),
        padding: '0 16px'
    },
    flexContainer: {
        display: 'flex'
    },
    row: {
        '&:not(.ReactVirtualized__Table__headerRow):hover': {
            backgroundColor: `${theme.quinary}`
        }
    },
    rowClickable: {
        '&:not(.ReactVirtualized__Table__headerRow):hover': {
            cursor: 'pointer',
            backgroundColor: `${theme.quinary}`
        }
    },
    noRowLeftPadding: {
        paddingLeft: '0px !important'
    }
}))

export const TableVirtualized = ({
    data,
    columns,
    onLoadMore,
    isLoadingMore,
    sorting,
    onSort,
    onRowClick,
    isLoading,
    rowHeight,
    noRowLeftPadding,
    disableHeader,
    height
}) => {
    const classes = useStyles()

    const [name, direction] = useMemo(
        () => (sorting ? sorting.split('.') : []),
        [sorting]
    )

    const handleSort = ({ sortBy }) => {
        if (!onSort) return

        if (!sorting || name !== sortBy) {
            return onSort(`${sortBy}.desc`)
        }

        onSort(direction === 'desc' ? `${sortBy}.asc` : '')
    }

    if (!data) {
        return null
    }

    return (
        <div className={classes.wrapper} style={{ height: height ?? '100%' }}>
            {isLoadingMore || isLoading ? <Loading overlay /> : null}
            <InfiniteLoader
                isRowLoaded={({ index }) => !!data[index]}
                loadMoreRows={() => onLoadMore?.()}
                rowCount={1000000}
            >
                {({ onRowsRendered, registerChild }) => (
                    <AutoSizer>
                        {({ width, height }) => {
                            const minFullWidth = getFullMinWidth(columns)
                            let calculatedFullWidth = width
                            const isMinFullWidthUsed = minFullWidth > width
                            if (isMinFullWidthUsed) {
                                calculatedFullWidth = minFullWidth
                            }
                            return (
                                <Table
                                    ref={registerChild}
                                    onRowsRendered={onRowsRendered}
                                    headerHeight={48}
                                    width={calculatedFullWidth}
                                    height={height}
                                    className={classes.root}
                                    headerClassName={classes.header}
                                    gridClassName={classes.grid}
                                    rowHeight={rowHeight ?? 48}
                                    sortBy={name}
                                    sortDirection={
                                        direction
                                            ? direction.toUpperCase()
                                            : undefined
                                    }
                                    sort={handleSort}
                                    rowClassName={({ index }) =>
                                        classNames(
                                            index % 2
                                                ? classes.evenRow
                                                : classes.oddRow,
                                            onRowClick
                                                ? classes.rowClickable
                                                : classes.row,
                                            noRowLeftPadding
                                                ? classes.noRowLeftPadding
                                                : ''
                                        )
                                    }
                                    rowRenderer={defaultTableRowRenderer}
                                    rowCount={data.length}
                                    rowGetter={({ index }) => data[index]}
                                    onRowClick={onRowClick}
                                    disableHeader={disableHeader}
                                >
                                    {columns.map(
                                        ({
                                            label,
                                            key,
                                            render,
                                            sortable,
                                            width: colWidth,
                                            alignCenter,
                                            maxWidth,
                                            headerClasses,
                                            paddingLeft,
                                            minWidth,
                                            headerRenderer
                                        }) => {
                                            return (
                                                <Column
                                                    key={key}
                                                    label={label}
                                                    cellRenderer={render}
                                                    dataKey={key}
                                                    disableSort={!sortable}
                                                    style={{
                                                        paddingLeft:
                                                            paddingLeft ||
                                                            undefined
                                                    }}
                                                    width={
                                                        isMinFullWidthUsed
                                                            ? minWidth
                                                            : width * colWidth
                                                    }
                                                    maxWidth={maxWidth}
                                                    minWidth={minWidth}
                                                    className={classNames({
                                                        [classes.colCentered]: alignCenter,
                                                        [classes.flexContainer]: true
                                                    })}
                                                    headerClassName={classNames(
                                                        {
                                                            [classes.colCentered]: alignCenter,
                                                            [classes.flexContainer]: true
                                                        },
                                                        headerClasses
                                                    )}
                                                    headerRenderer={
                                                        headerRenderer ||
                                                        undefined
                                                    }
                                                />
                                            )
                                        }
                                    )}
                                </Table>
                            )
                        }}
                    </AutoSizer>
                )}
            </InfiniteLoader>
        </div>
    )
}

const getFullMinWidth = columns => {
    // sum all columns minWidth, add first column left margin 10px
    let fullMinWidth = columns.reduce((acc, c) => acc + c.minWidth + 10, 10)
    // add row left padding 16px and right padding 1px
    fullMinWidth += 17
    return fullMinWidth
}
